import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Smartphone, Apple, Save, RefreshCw } from 'lucide-react';
import { settingsApi, AppVersionConfig } from '../services/api';

export default function SettingsPage() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['versionConfig'],
    queryFn: settingsApi.getVersionConfig,
  });

  const [form, setForm] = useState({
    iosLatestVersion: '',
    iosForceUpdate: false,
    androidLatestVersion: '',
    androidForceUpdate: false,
  });

  useEffect(() => {
    if (config) {
      setForm({
        iosLatestVersion: config.iosLatestVersion,
        iosForceUpdate: config.iosForceUpdate,
        androidLatestVersion: config.androidLatestVersion,
        androidForceUpdate: config.androidForceUpdate,
      });
    }
  }, [config]);

  const mutation = useMutation({
    mutationFn: (updates: Partial<AppVersionConfig>) =>
      settingsApi.updateVersionConfig(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versionConfig'] });
      toast.success('Konfigurimi u ruajt me sukses');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Ruajtja deshtoi');
    },
  });

  const handleSave = () => {
    mutation.mutate(form);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Perditesimi i Aplikacionit</h1>
        <p className="text-sm text-gray-500 mt-1">
          Konfiguro versionin me te ri per cdo platforme. Kur versioni ketu eshte me i madh se ai ne telefon, perdoruesi do te njoftohet.
        </p>
      </div>

      {/* iOS Section */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <Apple className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">iOS</h2>
            <p className="text-sm text-gray-500">App Store</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Versioni me i ri
          </label>
          <input
            type="text"
            className="input-field max-w-xs"
            placeholder="p.sh. 5"
            value={form.iosLatestVersion}
            onChange={(e) => setForm({ ...form, iosLatestVersion: e.target.value })}
          />
        </div>

        <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-700">Perditesim i detyrueshem</p>
            <p className="text-xs text-gray-500">Perdoruesi nuk mund ta mbyll njoftimin</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.iosForceUpdate}
            onClick={() => setForm({ ...form, iosForceUpdate: !form.iosForceUpdate })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-barrikadave-300 focus-visible:ring-offset-2 ${
              form.iosForceUpdate ? 'bg-barrikadave-500' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                form.iosForceUpdate ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Android Section */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Android</h2>
            <p className="text-sm text-gray-500">Google Play Store</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Versioni me i ri
          </label>
          <input
            type="text"
            className="input-field max-w-xs"
            placeholder="p.sh. 5"
            value={form.androidLatestVersion}
            onChange={(e) => setForm({ ...form, androidLatestVersion: e.target.value })}
          />
        </div>

        <div className="mt-4 flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-700">Perditesim i detyrueshem</p>
            <p className="text-xs text-gray-500">Perdoruesi nuk mund ta mbyll njoftimin</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.androidForceUpdate}
            onClick={() => setForm({ ...form, androidForceUpdate: !form.androidForceUpdate })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-barrikadave-300 focus-visible:ring-offset-2 ${
              form.androidForceUpdate ? 'bg-barrikadave-500' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                form.androidForceUpdate ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className="btn-primary flex items-center gap-2"
          onClick={handleSave}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Ruaj ndryshimet
        </button>
      </div>

      {config?.updatedAt && (
        <p className="text-xs text-gray-400 text-right">
          Perditesuar per here te fundit: {new Date(config.updatedAt).toLocaleString('sq-AL')}
        </p>
      )}
    </div>
  );
}
